import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { FocalImage } from "@/components/ui/focal-image";
import type { RecipeBox } from "@/lib/types";
import { BookOpen, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import type { MouseEvent } from "react";

interface RecipeBoxCardProps {
    readonly recipeBox: RecipeBox;
    readonly onEdit?: (recipeBox: RecipeBox) => void;
}

export function RecipeBoxCard({ recipeBox, onEdit }: RecipeBoxCardProps) {
    const imageUrl = recipeBox.imageUrl || "https://picsum.photos/seed/placeholder/480/320";

    const handleEditClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        onEdit?.(recipeBox);
    };

    return (
        <Link to={`/recipe-box/${recipeBox.id}`} className="relative block h-full">
            <Card className="flex h-full flex-col text-xs transition-all duration-200 ease-in-out hover:shadow-lg hover:-translate-y-0.5">
                <CardHeader className="p-0">
                    <div className="relative aspect-square overflow-hidden">
                        <FocalImage
                            src={imageUrl}
                            alt={recipeBox.title}
                            className="absolute inset-0 h-full w-full rounded-t-lg"
                            data-ai-hint={recipeBox.imageHint}
                            focalPointX={recipeBox.imageFocalPointX}
                            focalPointY={recipeBox.imageFocalPointY}
                            zoom={recipeBox.imageZoom}
                        />
                    </div>
                </CardHeader>
                <CardContent className="flex-1 p-1.5">
                    <CardTitle className="font-headline text-sm leading-tight truncate">{recipeBox.title}</CardTitle>
                    <CardDescription className="mt-0.5 text-xs line-clamp-1">{recipeBox.description}</CardDescription>
                </CardContent>
                <CardFooter className="flex items-center p-1.5 pt-0 text-xs text-muted-foreground">
                    <BookOpen className="mr-1.5 h-3 w-3" />
                    <span>{recipeBox.recipeCount} recipes</span>
                </CardFooter>
            </Card>

            {onEdit ? (
                <Button
                    variant="secondary"
                    size="icon"
                    className="absolute right-2 top-2 h-7 w-7 rounded-full bg-background/90 backdrop-blur-sm shadow-sm transition-all duration-200 hover:bg-background hover:shadow-md hover:scale-110 active:scale-100"
                    onClick={handleEditClick}
                    aria-label={`Edit ${recipeBox.title}`}
                >
                    <Pencil className="h-3.5 w-3.5" />
                </Button>
            ) : null}
        </Link>
    );
}
